import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Chip,
    CircularProgress,
    Button,
    IconButton,
    ButtonGroup,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Collapse,
    Divider,
    TextField,
    Tooltip,
    Badge,
    Tabs,
    Tab,
    TablePagination,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import SpeedIcon from '@mui/icons-material/Speed';
import HubIcon from '@mui/icons-material/Hub';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import CloseIcon from '@mui/icons-material/Close';
import {
    ComposedChart,
    Area,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RTooltip,
    Legend,
    ResponsiveContainer,
    LabelList,
} from 'recharts';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getDashboardOverview, getDashboardFilters, getDashboardRadar, getEditorialIntelligences, getDashboardUserFilters } from '../../../utils/api';
import { createNotification } from '../../../utils/alerts';
import {
    buildSectorFilterGroups,
    expandSectorSelections,
    isSectorGroupSelected,
    toggleSectorGroupSelection,
} from '../../../utils/sectorFilterGroups';
import ResponsiveAdminPage from '../../../components/admin/ResponsiveAdminPage';
import './dashboards.scss';

// ── Paleta ───────────────────────────────────────────────────────────────────
const PRIMARY = '#f25d27';
const PRIMARY_DARK = '#d44e1f';
const CHART_PALETTE = ['#f25d27', '#1a56db', '#10b981', '#8b5cf6', '#f59e0b', '#06b6d4', '#84cc16', '#ef4444'];
const CLASS_COLORS = ['#1a56db', '#8b5cf6', '#f59e0b', '#06b6d4', '#84cc16', '#ef4444', '#ec4899', '#14b8a6'];
const MAX_BAR_ITEMS = 10;
const BAR_HEIGHT = 30;

const PERIOD_OPTIONS = [
    { key: 'yesterday', label: 'Ontem', days: 1, oneDay: true },
    { key: 'today', label: 'Hoje', days: 0, oneDay: true },
    { key: '7d', label: 'Últimos 7 dias', days: 7 },
    { key: '15d', label: 'Últimos 15 dias', days: 15 },
    { key: '30d', label: 'Últimos 30 dias', days: 30 },
];

const isOneDayQuickPeriod = (key) => Boolean(PERIOD_OPTIONS.find((o) => o.key === key)?.oneDay);

const computePeriodRange = (key) => {
    const opt = PERIOD_OPTIONS.find((o) => o.key === key);
    if (!opt) return null;
    const end = new Date();
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    if (key === 'yesterday') {
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        return { date_from: start.toISOString(), date_to: end.toISOString() };
    }
    if (opt.days > 0) start.setDate(start.getDate() - opt.days);
    return { date_from: start.toISOString(), date_to: end.toISOString() };
};

const computePeriodDateRange = (key) => {
    const opt = PERIOD_OPTIONS.find((o) => o.key === key);
    if (!opt) return null;
    const end = new Date();
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    if (key === 'yesterday') {
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
    } else if (opt.days > 0) {
        start.setDate(start.getDate() - opt.days);
    }
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return { dateFrom: fmt(start), dateTo: fmt(end) };
};

const INIT_FILTERS = {
    dateFrom: '', dateTo: '',
    sentiments: [],
    minConf: '', maxConf: '',
};

const DASHBOARD_RADAR_PAGE_SIZE_OPTIONS = [10, 25, 50];
const LAST_UPDATES_SCROLL_OFFSET = 260;

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const normalizeFilterState = (value = {}) => ({
    dateFrom: typeof value.dateFrom === 'string' ? value.dateFrom : '',
    dateTo: typeof value.dateTo === 'string' ? value.dateTo : '',
    sentiments: normalizeArray(value.sentiments),
    minConf: value.minConf ?? '',
    maxConf: value.maxConf ?? '',
});

const normalizeQuickPeriod = (value) => (
    PERIOD_OPTIONS.some((option) => option.key === value) ? value : 'today'
);

const normalizeGranularity = (value) => (
    ['hour', 'day', 'week', 'month'].includes(value) ? value : 'hour'
);

const normalizeNonNegativeInt = (value, fallback = 0) => (
    Number.isInteger(value) && value >= 0 ? value : fallback
);

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (str) => {
    if (!str) return '—';
    return new Date(str).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });
};

const fmtTimeTick = (str, gran) => {
    if (!str) return '';
    if (gran === 'hour') {
        const m = str.match(/T(\d{2}:\d{2})/);
        return m ? m[1] : str;
    }
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}` : str;
};

const fmtConf = (v) => (v != null ? `${(v * 100).toFixed(1)}%` : 'N/D');

const fmtChange = (v) => {
    if (v == null) return null;
    const value = Number(v);
    if (Number.isNaN(value)) return null;
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
};

const fmtPeriodDateBR = (value) => {
    if (!value) return '—';
    const str = String(value);
    const datePart = str.includes('T') ? str.split('T')[0] : str;
    const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
        return `${match[3]}/${match[2]}/${match[1]}`;
    }
    const parsed = new Date(str);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString('pt-BR');
    }
    return datePart;
};

const buildApiParams = (f, quick = {}) => {
    const p = {};
    if (f.dateFrom) p.date_from = `${f.dateFrom}T00:00:00Z`;
    if (f.dateTo) p.date_to = `${f.dateTo}T23:59:59Z`;
    if (!f.dateFrom && !f.dateTo && quick.period) {
        const range = computePeriodRange(quick.period);
        if (range) {
            p.date_from = range.date_from;
            p.date_to = range.date_to;
        }
    }
    if (f.sentiments?.length) p.document_sentiments = f.sentiments;
    if (f.minConf !== '') p.min_confidence = parseFloat(f.minConf);
    if (f.maxConf !== '') p.max_confidence = parseFloat(f.maxConf);
    if (quick.sources?.length) p.sources = quick.sources;
    if (quick.sectors?.length) p.sectors = quick.sectors;
    if (quick.recommendations?.length) p.publication_recommendation_primary_areas = quick.recommendations;
    if (quick.materialityLevel) p.materiality_level = quick.materialityLevel;
    if (quick.radarKeywords?.length) p.radar_keywords = quick.radarKeywords;
    return p;
};

// ── Micro-componentes ─────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label, labelFmt }) => {
    if (!active || !payload?.length) return null;
    const displayLabel = labelFmt ? labelFmt(label) : label;
    return (
        <Box sx={{
            background: '#fff', border: '1px solid #eee', borderRadius: '8px',
            p: 1.3, boxShadow: '0 4px 14px rgba(0,0,0,.10)', fontSize: '0.78rem', lineHeight: 1.7,
        }}>
            {displayLabel && <Box sx={{ fontWeight: 700, color: '#222', mb: 0.4 }}>{displayLabel}</Box>}
            {payload.map((entry, i) => {
                if (entry.value == null) return null;
                const val = entry.dataKey === 'avg_confidence' ? fmtConf(entry.value) : entry.value;
                return (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: entry.color || entry.fill }} />
                        <span style={{ color: '#555' }}>{entry.name}: <strong>{val}</strong></span>
                    </Box>
                );
            })}
        </Box>
    );
};

const BarTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const val = payload[0]?.value;
    if (val == null) return null;
    return (
        <Box sx={{
            background: 'rgba(40,40,40,0.92)',
            color: '#fff',
            borderRadius: '5px',
            px: 1.3,
            py: 0.7,
            fontSize: '0.75rem',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        }}>
            {label}: <strong>{Number(val).toLocaleString('pt-BR')}</strong> artigo{val !== 1 ? 's' : ''}
        </Box>
    );
};

const SourceChip = ({ label }) => (
    <Chip
        label={label}
        size="small"
        sx={{ backgroundColor: `${PRIMARY}18`, color: PRIMARY, fontSize: '0.7rem', height: 20, fontWeight: 600 }}
    />
);

// ── KPI Card ──────────────────────────────────────────────────────────────────
const ChangeBadge = ({ value, invert = false }) => {
    if (value == null) return null;
    const num = Number(value);
    if (Number.isNaN(num)) return null;
    const positive = invert ? num < 0 : num > 0;
    const neutral = num === 0;
    const color = neutral ? '#9e9e9e' : positive ? '#10b981' : '#ef4444';
    const Icon = neutral ? TrendingFlatIcon : positive ? TrendingUpIcon : TrendingDownIcon;
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.3,
            backgroundColor: `${color}15`, color, px: 0.9, py: 0.2,
            borderRadius: '12px', fontSize: '0.7rem', fontWeight: 700,
        }}>
            <Icon sx={{ fontSize: 13 }} />
            {fmtChange(value)}
        </Box>
    );
};

const KpiCard = ({ title, value, helper, change, invertChange, icon, accentColor, onClick }) => (
    <Paper
        onClick={onClick}
        sx={{
            p: 2.2,
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: onClick ? 'pointer' : 'default',
            transition: 'transform 0.18s, box-shadow 0.18s',
            '&:hover': onClick
                ? { transform: 'translateY(-2px)', boxShadow: `0 6px 18px ${accentColor}22` }
                : { transform: 'translateY(-2px)', boxShadow: '0 6px 18px rgba(0,0,0,0.08)' },
        }}>
        <Box sx={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: 3,
            background: `linear-gradient(90deg, ${accentColor} 0%, ${accentColor}55 100%)`,
        }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.2 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: '10px',
                backgroundColor: `${accentColor}15`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {React.cloneElement(icon, { sx: { color: accentColor, fontSize: 20 } })}
            </Box>
            {change != null && <ChangeBadge value={change} invert={invertChange} />}
        </Box>
        <Typography variant="caption" sx={{
            color: '#888', fontSize: '0.72rem', fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.3,
        }}>
            {title}
        </Typography>
        <Typography variant="h4" sx={{
            fontWeight: 700, color: '#1a1a1a', lineHeight: 1.1, mb: 0.4,
            fontSize: { xs: '1.55rem', md: '1.75rem' },
        }}>
            {value}
        </Typography>
        {helper && (
            <Typography variant="caption" sx={{ color: '#999', fontSize: '0.72rem' }}>
                {helper}
            </Typography>
        )}
    </Paper>
);

// ── Cabeçalho de seção ────────────────────────────────────────────────────────
const SectionHead = ({ title, subtitle, action }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '0.95rem' }}>{title}</Typography>
            {subtitle && (
                <Typography variant="caption" sx={{ color: '#999', fontSize: '0.72rem' }}>{subtitle}</Typography>
            )}
        </Box>
        {action}
    </Box>
);

// ── Tick truncado no YAxis ─────────────────────────────────────────────────────
const TruncatedTick = ({ x, y, payload, maxChars = 18 }) => {
    const raw = payload?.value || '';
    const label = raw.length > maxChars ? `${raw.slice(0, maxChars - 1)}…` : raw;
    return (
        <g transform={`translate(${x},${y})`}>
            <title>{raw}</title>
            <text x={0} y={0} dy={4} textAnchor="end" fill="#555" fontSize={11}>
                {label}
            </text>
        </g>
    );
};

// ── Distribuição: Barra de Carregamento (Por Fonte) ────────────────────────────
const DistSourceBar = ({ data, onItemClick }) => {
    const sorted = useMemo(() => {
        if (!data?.length) return [];
        return [...data].sort((a, b) => (b.count || 0) - (a.count || 0)).slice(0, MAX_BAR_ITEMS);
    }, [data]);
    const total = sorted.reduce((s, d) => s + (d.count || 0), 0);

    return (
        <Paper sx={{
            p: 2, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0', height: '100%',
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#333', fontSize: '0.85rem' }}>
                    Por Fonte
                </Typography>
                {total > 0 && (
                    <Chip
                        label={total.toLocaleString('pt-BR')}
                        size="small"
                        sx={{ backgroundColor: `${PRIMARY}15`, color: PRIMARY, fontSize: '0.65rem', height: 18, fontWeight: 700 }}
                    />
                )}
            </Box>
            {!sorted.length ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80, color: '#ccc', fontSize: '0.82rem' }}>
                    Sem dados
                </Box>
            ) : (
                sorted.map((item, idx) => {
                    const pct = total > 0 ? (item.count / total) * 100 : 0;
                    const color = PRIMARY;
                    return (
                        <Box
                            key={idx}
                            onClick={() => onItemClick && onItemClick(item.label)}
                            sx={{ mb: 1.8, cursor: onItemClick ? 'pointer' : 'default', borderRadius: '6px', p: '2px 4px', mx: '-4px', '&:hover': onItemClick ? { backgroundColor: `${PRIMARY}08` } : {} }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#444', fontSize: '0.76rem' }} noWrap>
                                    {item.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem', flexShrink: 0, ml: 1 }}>
                                    {pct.toFixed(1)}%
                                </Typography>
                            </Box>
                            <Tooltip title={`${item.label}: ${item.count.toLocaleString('pt-BR')} artigo${item.count !== 1 ? 's' : ''}`} placement="top">
                                <Box sx={{ height: 7, backgroundColor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden', cursor: 'inherit' }}>
                                    <Box sx={{
                                        height: '100%',
                                        width: `${pct}%`,
                                        backgroundColor: color,
                                        borderRadius: '4px',
                                        transition: 'width 0.6s ease',
                                    }} />
                                </Box>
                            </Tooltip>
                        </Box>
                    );
                })
            )}
        </Paper>
    );
};

// ── Distribuição: Barra Horizontal ────────────────────────────────────────────
const DistHorizBar = ({ title, data, color, yAxisWidth = 120, maxLabelChars = 18, onItemClick }) => {
    const sorted = useMemo(() => {
        if (!data?.length) return [];
        return [...data].sort((a, b) => (b.count || 0) - (a.count || 0));
    }, [data]);
    const visible = sorted.slice(0, MAX_BAR_ITEMS);
    const truncated = sorted.length > MAX_BAR_ITEMS;
    const totalAll = sorted.reduce((s, d) => s + (d.count || 0), 0);
    const chartHeight = Math.max(visible.length * BAR_HEIGHT, 80);

    return (
        <Paper sx={{
            p: 2, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0', height: '100%',
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#333', fontSize: '0.85rem' }}>
                    {title}
                </Typography>
                {sorted.length > 0 && (
                    <Tooltip title={truncated ? `${sorted.length} categorias · total ${totalAll.toLocaleString('pt-BR')}` : `total ${totalAll.toLocaleString('pt-BR')}`}>
                        <Chip
                            label={truncated ? `Top ${MAX_BAR_ITEMS} de ${sorted.length}` : totalAll.toLocaleString('pt-BR')}
                            size="small"
                            sx={{ backgroundColor: `${color || PRIMARY}15`, color: color || PRIMARY, fontSize: '0.65rem', height: 18, fontWeight: 700 }}
                        />
                    </Tooltip>
                )}
            </Box>
            {!visible.length ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80, color: '#ccc', fontSize: '0.82rem' }}>
                    Sem dados
                </Box>
            ) : (
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart layout="vertical" data={visible} margin={{ top: 0, right: 44, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f4f4f4" />
                        <XAxis type="number" hide />
                        <YAxis
                            type="category"
                            dataKey="label"
                            width={yAxisWidth}
                            interval={0}
                            tick={<TruncatedTick maxChars={maxLabelChars} />}
                            tickLine={false}
                            axisLine={false}
                        />
                        <RTooltip content={<BarTooltip />} cursor={{ fill: '#f5f5f5' }} />
                        <Bar
                            dataKey="count"
                            name="Artigos"
                            fill={color || PRIMARY}
                            radius={[0, 4, 4, 0]}
                            barSize={12}
                            cursor={onItemClick ? 'pointer' : 'default'}
                            onClick={(d) => onItemClick && onItemClick(d.label)}
                        >
                            <LabelList dataKey="count" position="right" style={{ fontSize: 11, fill: '#888', fontWeight: 600 }} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}
        </Paper>
    );
};

// ── Distribuição: Bolhas ───────────────────────────────────────────────────────
const DistBubble = ({ title, data, onItemClick }) => {
    const POS = [
        { x: 8,  y: 8  },
        { x: 48, y: 24 },
        { x: 22, y: 52 },
        { x: 65, y: 6  },
        { x: 70, y: 50 },
        { x: 2,  y: 62 },
        { x: 36, y: 30 },
        { x: 55, y: 62 },
    ];

    const items = useMemo(() => {
        if (!data?.length) return [];
        return [...data].sort((a, b) => (b.count || 0) - (a.count || 0)).slice(0, 8);
    }, [data]);

    const maxCount = items[0]?.count || 1;
    const minCount = items[items.length - 1]?.count || 1;

    const getSize = (count) => {
        const ratio = (count - minCount) / Math.max(maxCount - minCount, 1);
        return Math.round(56 + ratio * 74);
    };

    const formatLabel = (label) => {
        if (label.length <= 10) return label;
        const words = label.split(' ');
        if (words.length < 2) return label.length > 14 ? `${label.slice(0, 13)}…` : label;
        const half = Math.ceil(words.length / 2);
        return `${words.slice(0, half).join(' ')}\n${words.slice(half).join(' ')}`;
    };

    return (
        <Paper sx={{
            borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0', height: '100%', overflow: 'hidden',
        }}>
            <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#333', fontSize: '0.85rem' }}>
                    {title}
                </Typography>
            </Box>
            <Box sx={{ px: 2, pb: 2 }}>
                <Box sx={{
                    position: 'relative', height: 260,
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    overflow: 'hidden',
                }}>
                    {!items.length ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ccc', fontSize: '0.8rem' }}>
                            Sem dados
                        </Box>
                    ) : items.map((item, idx) => {
                        const size     = getSize(item.count);
                        const pos      = POS[idx] || { x: 40, y: 40 };
                        const bg       = `${CHART_PALETTE[idx % CHART_PALETTE.length]}18`;
                        const txtColor = CHART_PALETTE[idx % CHART_PALETTE.length];
                        const fontSize = size > 90 ? '13px' : size > 70 ? '12px' : '11px';
                        const label    = formatLabel(item.label);
                        return (
                            <Tooltip key={idx} title={`${item.label}: ${item.count.toLocaleString('pt-BR')}`}>
                                <Box
                                    onClick={() => onItemClick && onItemClick(item.label)}
                                    sx={{
                                        position: 'absolute',
                                        width: size, height: size,
                                        left: `${pos.x}%`, top: `${pos.y}%`,
                                        borderRadius: '50%',
                                        backgroundColor: bg,
                                        border: `1.5px solid ${txtColor}55`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize, fontWeight: 600, color: txtColor,
                                        textAlign: 'center', lineHeight: 1.3, p: '6px',
                                        cursor: 'pointer', whiteSpace: 'pre-line',
                                        transition: 'opacity 0.15s',
                                        '&:hover': { opacity: 0.8 },
                                    }}>
                                    {label}
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
            </Box>
        </Paper>
    );
};

// ── Filtros Rápidos: estilo chip miniatura ────────────────────────────────────
const miniChipSx = (selected, disabled = false) => ({
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex', alignItems: 'center',
    px: 1.2, py: 0.3, borderRadius: '16px', border: '1px solid',
    fontSize: '0.73rem', fontWeight: selected ? 600 : 500,
    lineHeight: 1.4, userSelect: 'none',
    opacity: disabled ? 0.45 : 1,
    transition: 'all 0.15s',
    borderColor: selected ? PRIMARY : '#e3e3e3',
    backgroundColor: selected ? PRIMARY : 'transparent',
    color: selected ? '#fff' : '#666',
    '&:hover': disabled ? {} : {
        borderColor: PRIMARY,
        backgroundColor: selected ? PRIMARY_DARK : `${PRIMARY}10`,
        color: selected ? '#fff' : PRIMARY,
    },
});

const FILTER_LABEL_SX = {
    fontWeight: 700, color: '#666', fontSize: '0.67rem',
    textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.8,
};

const ChipRow = ({ label, children }) => (
    <Box>
        <Typography variant="caption" sx={FILTER_LABEL_SX}>{label}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>{children}</Box>
    </Box>
);

const CompactUpdateMetaField = ({ label, value }) => (
    <Typography sx={{ fontSize: '0.74rem', color: '#555', lineHeight: 1.3 }}>
        <Box component="span" sx={{ fontWeight: 700, color: '#8a8a8a' }}>
            {label}:
        </Box>{' '}
        {value || '—'}
    </Typography>
);

const CompactLatestUpdateCard = ({
    item,
    onClick,
    sourceOverride = '',
    recommendationOverride = '',
    radarOverride = '',
}) => {
    const isUnder768 = useMediaQuery('(max-width:767px)');
    const isUnder600 = useMediaQuery('(max-width:599px)');

    const sourceValue = sourceOverride || item.source || '—';
    const recommendationValue = recommendationOverride || item.recommendation || item.publication_recommendation || '—';
    const radarValue = radarOverride || item.radar_keyword || '—';
    const sectorValue = item.sector || '—';
    const materialityValue = item.materiality_level || '—';

    return (
        <Paper
            elevation={0}
            onClick={onClick}
            sx={{
                p: 1.2,
                borderRadius: '8px',
                border: '1px solid #ececec',
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': onClick ? { backgroundColor: `${PRIMARY}08` } : undefined,
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#8a8a8a' }}>
                        Fonte:
                    </Typography>
                    <Chip
                        label={sourceValue}
                        size="small"
                        sx={{
                            backgroundColor: PRIMARY,
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            height: 22,
                            maxWidth: 'calc(100% - 56px)',
                            '& .MuiChip-label': { px: 1.1, overflow: 'hidden', textOverflow: 'ellipsis' },
                        }}
                    />
                </Box>

                <Typography sx={{ fontSize: '0.78rem', color: '#222', lineHeight: 1.35 }}>
                    <Box component="span" sx={{ fontWeight: 700, color: '#8a8a8a' }}>
                        Titulo:
                    </Box>{' '}
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 600,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            verticalAlign: 'top',
                        }}
                    >
                        {item.title || '—'}
                    </Box>
                </Typography>

                {isUnder600 ? (
                    <Box sx={{ display: 'grid', gap: 0.35 }}>
                        <CompactUpdateMetaField label="Setor" value={sectorValue} />
                        <CompactUpdateMetaField label="Recomendação" value={recommendationValue} />
                        <CompactUpdateMetaField label="Radar" value={radarValue} />
                        <CompactUpdateMetaField label="Materialidade" value={materialityValue} />
                    </Box>
                ) : isUnder768 ? (
                    <Box sx={{ display: 'grid', gap: 0.45 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.8 }}>
                            <CompactUpdateMetaField label="Setor" value={sectorValue} />
                            <CompactUpdateMetaField label="Recomendação" value={recommendationValue} />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.8 }}>
                            <CompactUpdateMetaField label="Radar" value={radarValue} />
                            <CompactUpdateMetaField label="Materialidade" value={materialityValue} />
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.8 }}>
                        <CompactUpdateMetaField label="Setor" value={sectorValue} />
                        <CompactUpdateMetaField label="Recomendação" value={recommendationValue} />
                        <CompactUpdateMetaField label="Radar" value={radarValue} />
                        <CompactUpdateMetaField label="Materialidade" value={materialityValue} />
                    </Box>
                )}

                <CompactUpdateMetaField label="Data de publicação" value={fmtDate(item.published_at)} />
            </Box>
        </Paper>
    );
};

// ── Tabela: Últimas Atualizações (Overview) ───────────────────────────────────
const LatestUpdatesTable = ({ items, onOpenItem }) => {
    const isSmallScreen = useMediaQuery('(max-width:1024px)');

    return (
        <Paper sx={{
            borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0', overflow: 'hidden',
        }}>
            <Box sx={{
                px: 2.2, py: 1.4, display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', borderBottom: '1px solid #f4f4f4',
                background: `linear-gradient(90deg, ${PRIMARY}08 0%, transparent 100%)`,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <Box sx={{
                        width: 30, height: 30, borderRadius: '8px',
                        backgroundColor: `${PRIMARY}18`, color: PRIMARY,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <ArticleIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#222', fontSize: '0.88rem' }}>
                            Últimas Atualizações
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem' }}>
                            10 publicações mais recentes no período
                        </Typography>
                    </Box>
                </Box>
                <Chip
                    label={items?.length ?? 0}
                    size="small"
                    sx={{ backgroundColor: PRIMARY, color: '#fff', fontWeight: 700, height: 22, fontSize: '0.72rem', minWidth: 28 }}
                />
            </Box>
            {isSmallScreen ? (
                <Box sx={{ p: 1.2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {!items?.length ? (
                        <Box sx={{ py: 2.5, textAlign: 'center', color: '#bbb', fontSize: '0.82rem' }}>
                            Nenhuma atualização recente
                        </Box>
                    ) : items.map((item, idx) => (
                        <CompactLatestUpdateCard
                            key={idx}
                            item={item}
                            onClick={item.id ? () => onOpenItem(item.id) : undefined}
                        />
                    ))}
                </Box>
            ) : (
            <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { backgroundColor: '#fafafa', fontWeight: 700, fontSize: '0.7rem', color: '#888', borderBottom: '1px solid #f0f0f0', py: 1, textTransform: 'uppercase', letterSpacing: 0.4 } }}>
                            <TableCell sx={{ width: 120 }}>Fonte</TableCell>
                            <TableCell>Título</TableCell>
                            <TableCell sx={{ width: 130 }}>Data de publicação</TableCell>
                            <TableCell sx={{ width: 150 }}>Setor</TableCell>
                            <TableCell sx={{ width: 180 }}>Recomendação</TableCell>
                            <TableCell sx={{ width: 120 }}>Materialidade</TableCell>
                            <TableCell sx={{ width: 130 }}>Radar</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!items?.length ? (
                            <TableRow>
                                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: '#bbb', fontSize: '0.82rem' }}>
                                    Nenhuma atualização recente
                                </TableCell>
                            </TableRow>
                        ) : items.map((item, idx) => (
                            <TableRow
                                key={idx}
                                onClick={() => item.id && onOpenItem(item.id)}
                                sx={{
                                    '& td': { borderBottom: '1px solid #f6f6f6', py: 1.1 },
                                    cursor: item.id ? 'pointer' : 'default',
                                    '&:hover': { backgroundColor: item.id ? `${PRIMARY}0D` : `${PRIMARY}05` },
                                }}
                            >
                                <TableCell>
                                    <Chip
                                        label={item.source || '—'}
                                        size="small"
                                        sx={{ backgroundColor: PRIMARY, color: '#fff', fontWeight: 600, fontSize: '0.75rem' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Tooltip title={item.title} placement="top-start">
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 300, fontSize: '0.82rem', color: '#222', fontWeight: 500 }}>
                                            {item.title}
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.78rem', color: '#666', whiteSpace: 'nowrap' }}>{fmtDate(item.published_at)}</TableCell>
                                <TableCell sx={{ fontSize: '0.78rem', color: '#444' }}>{item.sector || '—'}</TableCell>
                                <TableCell sx={{ fontSize: '0.78rem', color: '#444' }}>{item.recommendation || '—'}</TableCell>
                                <TableCell sx={{ fontSize: '0.78rem', color: '#444' }}>{item.materiality_level || '—'}</TableCell>
                                <TableCell sx={{ fontSize: '0.78rem', color: '#444' }}>{item.radar_keyword || '—'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
            )}
        </Paper>
    );
};

// ── Aba "Todos" do Radar: busca /admin/editorial-intelligence/?is_radar=true ──
const RadarAllTab = ({
    filterParams,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    onOpenItem,
}) => {
    const isSmallScreen = useMediaQuery('(max-width:1024px)');
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const prevFilterRef = useRef(filterParams);

    useEffect(() => {
        let effectivePage = page;
        const filtersChanged = prevFilterRef.current !== filterParams;

        if (filtersChanged) {
            prevFilterRef.current = filterParams;
            effectivePage = 0;
            if (page !== 0) {
                onPageChange(0);
                return;
            }
        }

        let cancelled = false;
        const fetchAll = async () => {
            setLoading(true);
            try {
                const params = {
                    is_radar: true,
                    order_by: 'published_at',
                    order_direction: 'desc',
                    page: effectivePage,
                    page_size: pageSize,
                };
                if (filterParams.date_from) params.publish_at_start = filterParams.date_from;
                if (filterParams.date_to)   params.publish_at_end   = filterParams.date_to;
                if (filterParams.sources?.length) params.sources = filterParams.sources;
                if (filterParams.sectors?.length) params.sectors = filterParams.sectors;
                if (filterParams.publication_recommendation_primary_areas?.length)
                    params.publication_recommendation_primary_areas = filterParams.publication_recommendation_primary_areas;
                if (filterParams.materiality_level) params.materiality_level = filterParams.materiality_level;
                if (filterParams.radar_keywords?.length) params.radar_keywords = filterParams.radar_keywords;

                const data = await getEditorialIntelligences(params);
                if (!cancelled) {
                    setItems(data.editorial_intelligences ?? []);
                    setTotal(data.total ?? 0);
                }
            } catch {
                // falha silenciosa
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchAll();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterParams, page, pageSize]);

    return (
        <Box>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ color: PRIMARY }} />
                </Box>
            ) : items.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, color: '#bbb', fontSize: '0.82rem' }}>
                    Nenhuma atualização recente
                </Box>
            ) : (
                <>
                    {isSmallScreen ? (
                        <Box sx={{ p: 1.2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {items.map((item, idx) => (
                                <CompactLatestUpdateCard
                                    key={item.id ?? idx}
                                    item={item}
                                    recommendationOverride={item.publication_recommendation || ''}
                                    onClick={item.id ? () => onOpenItem(item.id) : undefined}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ '& th': { backgroundColor: '#fff', fontWeight: 700, fontSize: '0.68rem', color: '#aaa', borderBottom: '1px solid #f0f0f0', py: 0.8, textTransform: 'uppercase', letterSpacing: 0.4 } }}>
                                        <TableCell sx={{ width: 120 }}>Fonte</TableCell>
                                        <TableCell>Título</TableCell>
                                        <TableCell sx={{ width: 150 }}>Data de publicação</TableCell>
                                        <TableCell sx={{ width: 150 }}>Setor</TableCell>
                                        <TableCell sx={{ width: 180 }}>Recomendação</TableCell>
                                        <TableCell sx={{ width: 120 }}>Materialidade</TableCell>
                                        <TableCell sx={{ width: 140 }}>Radar</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item, idx) => (
                                        <TableRow
                                            key={item.id ?? idx}
                                            onClick={() => item.id && onOpenItem(item.id)}
                                            sx={{
                                                '& td': { borderBottom: '1px solid #f6f6f6', py: 1.1 },
                                                cursor: item.id ? 'pointer' : 'default',
                                                '&:hover': { backgroundColor: item.id ? `${PRIMARY}0D` : `${PRIMARY}05` },
                                            }}
                                        >
                                            <TableCell>
                                                <Chip
                                                    label={item.source || '—'}
                                                    size="small"
                                                    sx={{ backgroundColor: PRIMARY, color: '#fff', fontWeight: 600, fontSize: '0.72rem' }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={item.title} placement="top-start">
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: 380, fontSize: '0.82rem', color: '#222', fontWeight: 500 }}>
                                                        {item.title}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.78rem', color: '#666', whiteSpace: 'nowrap' }}>{fmtDate(item.published_at)}</TableCell>
                                            <TableCell sx={{ fontSize: '0.78rem', color: '#444' }}>{item.sector || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: '0.78rem', color: '#444' }}>{item.publication_recommendation || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: '0.78rem', color: '#444' }}>{item.materiality_level || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: '0.78rem', color: '#444' }}>{item.radar_keyword || '—'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, newPage) => onPageChange(newPage)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={(e) => {
                            onPageSizeChange(parseInt(e.target.value, 10));
                            onPageChange(0);
                        }}
                        rowsPerPageOptions={DASHBOARD_RADAR_PAGE_SIZE_OPTIONS}
                        labelRowsPerPage="Itens por página:"
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`}
                        sx={{
                            borderTop: '1px solid #f0f0f0',
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.78rem' },
                        }}
                    />
                </>
            )}
        </Box>
    );
};

// ── Últimas Atualizações: Radar (abas por classificação, tabelas por fonte) ───
const RadarLastUpdates = ({
    groups,
    filterParams,
    hasHydratedData,
    classTab,
    onClassTabChange,
    radarAllPage,
    radarAllPageSize,
    onRadarAllPageChange,
    onRadarAllPageSizeChange,
    onOpenItem,
}) => {
    const isSmallScreen = useMediaQuery('(max-width:1024px)');

    const safeGroups = groups ?? [];

    useEffect(() => {
        if (!hasHydratedData) return;
        if (classTab > safeGroups.length) {
            onClassTabChange(0);
        }
    }, [classTab, hasHydratedData, safeGroups.length, onClassTabChange]);

    // classTab 0 = aba "Todos"; classTab > 0 = grupos (índice classTab - 1)
    const activeGroup = classTab > 0 ? (safeGroups[classTab - 1] ?? safeGroups[0]) : null;
    const totalArticles = safeGroups.reduce((sum, g) =>
        sum + (g.sources || []).reduce((s, src) => s + (src.last_updates?.length || 0), 0), 0);

    return (
        <Paper sx={{
            borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0', overflow: 'hidden',
        }}>
            {/* Cabeçalho */}
            <Box sx={{
                px: 2.2, py: 1.4, display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', borderBottom: '1px solid #f4f4f4',
                background: `linear-gradient(90deg, ${PRIMARY}08 0%, transparent 100%)`,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <Box sx={{
                        width: 30, height: 30, borderRadius: '8px',
                        backgroundColor: `${PRIMARY}18`, color: PRIMARY,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <ArticleIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#222', fontSize: '0.88rem' }}>
                            Últimas Atualizações
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem' }}>
                            Publicações mais recentes por classificação e fonte
                        </Typography>
                    </Box>
                </Box>
                <Chip
                    label={totalArticles}
                    size="small"
                    sx={{ backgroundColor: PRIMARY, color: '#fff', fontWeight: 700, height: 22, fontSize: '0.72rem', minWidth: 28 }}
                />
            </Box>

            {/* Abas: "Todos" fixo + abas por classificação */}
            <Box sx={{ borderBottom: '1px solid #f0f0f0' }}>
                <Tabs
                    value={classTab}
                    onChange={(_, v) => onClassTabChange(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        px: 1,
                        minHeight: 40,
                        '& .MuiTabs-indicator': { backgroundColor: PRIMARY, height: 2 },
                        '& .MuiTab-root': {
                            textTransform: 'none', fontWeight: 500, fontSize: '0.8rem',
                            minHeight: 40, py: 0.5, px: 1.5, color: '#666',
                        },
                        '& .Mui-selected': { color: `${PRIMARY} !important`, fontWeight: 700 },
                    }}
                >
                    <Tab label="Todos" />
                    {safeGroups.map((g, i) => (
                        <Tab key={i + 1} label={g.radar_keyword} />
                    ))}
                </Tabs>
            </Box>

            {/* Conteúdo da aba "Todos" */}
            {classTab === 0 && (
                <RadarAllTab
                    filterParams={filterParams ?? {}}
                    page={radarAllPage}
                    pageSize={radarAllPageSize}
                    onPageChange={onRadarAllPageChange}
                    onPageSizeChange={onRadarAllPageSizeChange}
                    onOpenItem={onOpenItem}
                />
            )}

            {/* Conteúdo das abas por classificação */}
            {classTab > 0 && (
                safeGroups.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, color: '#bbb', fontSize: '0.82rem' }}>
                        Nenhuma atualização recente
                    </Box>
                ) : (activeGroup?.sources || []).length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, color: '#bbb', fontSize: '0.82rem' }}>
                        Nenhuma atualização para esta classificação
                    </Box>
                ) : (
                    (activeGroup?.sources || []).map((sourceGroup, sIdx) => (
                        <Box key={sIdx}>
                            {/* Header da fonte */}
                            <Box sx={{
                                px: 2.2, py: 1, display: 'flex', alignItems: 'center', gap: 1,
                                backgroundColor: '#fafafa', borderBottom: '1px solid #f4f4f4',
                            }}>
                                <SourceChip label={sourceGroup.source} />
                                <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.7rem' }}>
                                    {sourceGroup.last_updates?.length ?? 0} artigo{(sourceGroup.last_updates?.length ?? 0) !== 1 ? 's' : ''}
                                </Typography>
                            </Box>

                            {/* Tabela de artigos */}
                            {isSmallScreen ? (
                                <Box sx={{ p: 1.2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {!sourceGroup.last_updates?.length ? (
                                        <Box sx={{ textAlign: 'center', py: 2, color: '#bbb', fontSize: '0.8rem' }}>
                                            Sem artigos
                                        </Box>
                                    ) : sourceGroup.last_updates.map((item, idx) => (
                                        <CompactLatestUpdateCard
                                            key={idx}
                                            item={item}
                                            sourceOverride={sourceGroup.source || ''}
                                            recommendationOverride={item.recommendation || item.publication_recommendation || ''}
                                            radarOverride={item.radar_keyword || activeGroup?.radar_keyword || ''}
                                            onClick={item.id ? () => onOpenItem(item.id) : undefined}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ '& th': { backgroundColor: '#fff', fontWeight: 700, fontSize: '0.68rem', color: '#aaa', borderBottom: '1px solid #f0f0f0', py: 0.8, textTransform: 'uppercase', letterSpacing: 0.4 } }}>
                                                <TableCell>Título</TableCell>
                                                <TableCell sx={{ width: 130 }}>Data de publicação</TableCell>
                                                <TableCell sx={{ width: 150 }}>Setor</TableCell>
                                                <TableCell sx={{ width: 180 }}>Recomendação</TableCell>
                                                <TableCell sx={{ width: 120 }}>Materialidade</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {!sourceGroup.last_updates?.length ? (
                                                <TableRow>
                                                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 2.5, color: '#bbb', fontSize: '0.8rem' }}>
                                                        Sem artigos
                                                    </TableCell>
                                                </TableRow>
                                            ) : sourceGroup.last_updates.map((item, idx) => (
                                                <TableRow
                                                    key={idx}
                                                    onClick={() => item.id && onOpenItem(item.id)}
                                                    sx={{
                                                        '& td': { borderBottom: '1px solid #f6f6f6', py: 1.1 },
                                                        cursor: item.id ? 'pointer' : 'default',
                                                        '&:hover': { backgroundColor: item.id ? `${PRIMARY}0D` : `${PRIMARY}05` },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Tooltip title={item.title} placement="top-start">
                                                            <Typography variant="body2" noWrap sx={{ maxWidth: 380, fontSize: '0.82rem', color: '#222', fontWeight: 500 }}>
                                                                {item.title}
                                                            </Typography>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.78rem', color: '#666', whiteSpace: 'nowrap' }}>{fmtDate(item.published_at)}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.78rem', color: '#444' }}>{item.sector || '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.78rem', color: '#444' }}>{item.recommendation || '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.78rem', color: '#444' }}>{item.materiality_level || '—'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}

                            {sIdx < (activeGroup.sources.length - 1) && (
                                <Divider sx={{ borderColor: '#f0f0f0' }} />
                            )}
                        </Box>
                    ))
                )
            )}
        </Paper>
    );
};

// ── Componente Principal ───────────────────────────────────────────────────────
export default function AdminDashboardPage() {
    const isMobile = useMediaQuery('(max-width:600px)');
    const isQuickFiltersCompact = useMediaQuery('(max-width:1024px)');
    const isUnder768 = useMediaQuery('(max-width:767px)');
    const isUpTo1280 = useMediaQuery('(max-width:1280px)');
    const location = useLocation();
    const navigate = useNavigate();
    const restoredState = location.state?.dashboardState ?? null;

    // ── Aba ativa: 0 = Radar, 1 = Overview ────────────────────────────────────
    const [activeTab, setActiveTab] = useState(() => (restoredState?.activeTab === 1 ? 1 : 0));

    // Estado dos filtros avançados
    const [filterOptions, setFilterOptions] = useState({
        sources: [],
        sectors: [],
        document_sentiments: [],
        publication_recommendation_primary_areas: [],
        radar_keywords: [],
    });
    const [filtersOpen, setFiltersOpen] = useState(() => Boolean(restoredState?.filtersOpen));
    const [draft, setDraft] = useState(() => normalizeFilterState(restoredState?.draft));
    const [applied, setApplied] = useState(() => normalizeFilterState(restoredState?.applied));

    // Estado dos filtros rápidos
    const [quickPeriod, setQuickPeriod] = useState(() => normalizeQuickPeriod(restoredState?.quickPeriod));
    const [quickSources, setQuickSources] = useState(() => normalizeArray(restoredState?.quickSources));
    const [quickSectors, setQuickSectors] = useState(() => normalizeArray(restoredState?.quickSectors));
    const [quickRecommendations, setQuickRecommendations] = useState(() => normalizeArray(restoredState?.quickRecommendations));
    const [quickMaterialityLevel, setQuickMaterialityLevel] = useState(() => (
        typeof restoredState?.quickMaterialityLevel === 'string' ? restoredState.quickMaterialityLevel : ''
    ));
    const [quickRadarKeywords, setQuickRadarKeywords] = useState(() => normalizeArray(restoredState?.quickRadarKeywords));
    const [mobileQuickFiltersOpen, setMobileQuickFiltersOpen] = useState(() => Boolean(restoredState?.mobileQuickFiltersOpen));

    // Granularidade da série temporal (preferência do usuário)
    const [granularity, setGranularity] = useState(() => normalizeGranularity(restoredState?.granularity));

    // Estado da navegação interna da seção Radar > Últimas Atualizações
    const [radarClassTab, setRadarClassTab] = useState(() => normalizeNonNegativeInt(restoredState?.radarClassTab, 0));
    const [radarAllPage, setRadarAllPage] = useState(() => normalizeNonNegativeInt(restoredState?.radarAllPage, 0));
    const [radarAllPageSize, setRadarAllPageSize] = useState(() => (
        DASHBOARD_RADAR_PAGE_SIZE_OPTIONS.includes(restoredState?.radarAllPageSize)
            ? restoredState.radarAllPageSize
            : 10
    ));

    // Ao retornar de uma inteligência editorial (estado restaurado), rola
    // automaticamente até a seção "Últimas Atualizações" assim que os dados
    // da aba ativa terminarem de carregar, evitando que o usuário precise
    // procurar manualmente pela tabela/grupo que estava consultando.
    const lastUpdatesSectionRef = useRef(null);
    const hasScrolledToLastUpdatesRef = useRef(false);

    // Dados — Overview
    const [overview, setOverview] = useState(null);
    const [overviewLoading, setOverviewLoading] = useState(true);

    // Dados — Radar
    const [radar, setRadar] = useState(null);
    const [radarLoading, setRadarLoading] = useState(true);

    // Indica que os filtros (persistidos no servidor, quando existirem, e as
    // opções disponíveis) já foram carregados e aplicados. Enquanto for
    // falso, as requisições de Overview/Radar/Últimas Atualizações ficam em
    // espera, para que sejam disparadas apenas uma vez, já com os filtros
    // corretos — evitando uma primeira busca "vazia" seguida de outra.
    const [filtersInitialized, setFiltersInitialized] = useState(() => Boolean(restoredState));

    // ── Detecta se o período selecionado é de apenas 1 dia ────────────────────
    const isOneDayPeriod = useMemo(() => {
        if (applied.dateFrom && applied.dateTo) {
            return applied.dateFrom === applied.dateTo;
        }
        return isOneDayQuickPeriod(quickPeriod);
    }, [applied.dateFrom, applied.dateTo, quickPeriod]);

    // Granularidade efetiva: 'hora' obrigatório em 1 dia; 'hora' bloqueado em múltiplos dias
    const activeGranularity = useMemo(() => {
        if (isOneDayPeriod) return 'hour';
        if (granularity === 'hour') return 'day';
        return granularity;
    }, [isOneDayPeriod, granularity]);

    const sectorFilterGroups = useMemo(
        () => buildSectorFilterGroups(filterOptions.sectors),
        [filterOptions.sectors]
    );

    const expandedQuickSectors = useMemo(
        () => expandSectorSelections(quickSectors, filterOptions.sectors),
        [quickSectors, filterOptions.sectors]
    );

    // Ajusta preferência quando muda o tipo de período
    useEffect(() => {
        setGranularity(isOneDayPeriod ? 'hour' : 'day');
    }, [isOneDayPeriod]);

    // ── Carrega filtros persistidos do usuário, opções de filtro e aplica ──────
    // Ao acessar a tela (sem estado restaurado pela navegação interna), busca
    // primeiro os filtros salvos pelo usuário no servidor e, com a resposta em
    // mãos, busca as opções disponíveis para os filtros. Só então os filtros
    // salvos são aplicados ao estado local, para que as buscas de
    // Overview/Radar/Últimas Atualizações já saiam com os filtros corretos.
    useEffect(() => {
        let cancelled = false;

        const applyPersistedFilters = (data) => {
            if (!data) return;
            const sentiments = normalizeArray(data.document_sentiments);
            const minConf = data.min_confidence != null ? String(data.min_confidence) : '';
            const maxConf = data.max_confidence != null ? String(data.max_confidence) : '';
            setDraft((prev) => ({ ...prev, sentiments, minConf, maxConf }));
            setApplied((prev) => ({ ...prev, sentiments, minConf, maxConf }));
            setQuickSources(normalizeArray(data.sources));
            setQuickSectors(normalizeArray(data.sectors));
            setQuickRecommendations(normalizeArray(data.publication_recommendation_primary_areas));
            setQuickMaterialityLevel(typeof data.materiality_level === 'string' ? data.materiality_level : '');
            setQuickRadarKeywords(normalizeArray(data.radar_keywords));
            if (typeof data.granularity === 'string') {
                setGranularity(normalizeGranularity(data.granularity));
            }
            if (typeof data.is_radar === 'boolean') {
                setActiveTab(data.is_radar ? 0 : 1);
            }
        };

        const init = async () => {
            let persistedFilters = null;
            if (!restoredState) {
                try {
                    persistedFilters = await getDashboardUserFilters();
                } catch {
                    persistedFilters = null;
                }
            }

            try {
                const data = await getDashboardFilters();
                if (!cancelled) {
                    setFilterOptions({
                        sources: data.sources ?? [],
                        sectors: data.sectors ?? [],
                        document_sentiments: data.document_sentiments ?? [],
                        publication_recommendation_primary_areas: data.publication_recommendation_primary_areas ?? [],
                        radar_keywords: data.radar_keywords ?? [],
                    });
                }
            } catch {
                // falha silenciosa; filtros ficam vazios
            }

            if (!cancelled && persistedFilters) {
                applyPersistedFilters(persistedFilters);
            }
            if (!cancelled) setFiltersInitialized(true);
        };

        init();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buildQuickParams = () => buildApiParams(applied, {
        period: quickPeriod,
        sources: quickSources,
        sectors: expandedQuickSectors,
        recommendations: quickRecommendations,
        materialityLevel: quickMaterialityLevel,
        radarKeywords: quickRadarKeywords,
    });

    // ── Carrega dados da aba Overview ─────────────────────────────────────────
    useEffect(() => {
        if (!filtersInitialized) return;
        const fetchOverview = async () => {
            setOverviewLoading(true);
            try {
                const params = { ...buildQuickParams(), granularity: activeGranularity };
                const data = await getDashboardOverview(params);
                setOverview(data);
            } catch (err) {
                if (err.response?.status !== 401) {
                    createNotification('error', 'Erro ao carregar visão geral.', isMobile);
                }
            } finally {
                setOverviewLoading(false);
            }
        };
        fetchOverview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersInitialized, applied, activeGranularity, quickPeriod, quickSources, expandedQuickSectors, quickRecommendations, quickMaterialityLevel, quickRadarKeywords]);

    // ── Carrega dados da aba Radar ─────────────────────────────────────────────
    useEffect(() => {
        if (!filtersInitialized) return;
        const fetchRadar = async () => {
            setRadarLoading(true);
            try {
                const params = { ...buildQuickParams(), granularity: activeGranularity };
                const data = await getDashboardRadar(params);
                setRadar(data);
            } catch (err) {
                if (err.response?.status !== 401) {
                    createNotification('error', 'Erro ao carregar Radar.', isMobile);
                }
            } finally {
                setRadarLoading(false);
            }
        };
        fetchRadar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersInitialized, applied, activeGranularity, quickPeriod, quickSources, expandedQuickSectors, quickRecommendations, quickMaterialityLevel, quickRadarKeywords]);

    // ── Rola até "Últimas Atualizações" ao voltar de uma inteligência editorial ──
    useEffect(() => {
        if (!restoredState || hasScrolledToLastUpdatesRef.current) return;
        const hasData = activeTab === 0 ? Boolean(radar) : Boolean(overview);
        const isLoading = activeTab === 0 ? radarLoading : overviewLoading;
        if (!hasData || isLoading) return;

        hasScrolledToLastUpdatesRef.current = true;
        const timer = setTimeout(() => {
            const el = lastUpdatesSectionRef.current;
            if (!el) return;
            // Desce um pouco além do topo da seção, para que ela não fique
            // colada na borda superior da tela (fica mais confortável de ler).
            const targetY = el.getBoundingClientRect().top + window.scrollY + LAST_UPDATES_SCROLL_OFFSET;
            window.scrollTo({ top: targetY, behavior: 'smooth' });
        }, 150);
        return () => clearTimeout(timer);
    }, [restoredState, activeTab, radar, overview, radarLoading, overviewLoading]);

    const setDraftField = (key) => (val) => setDraft((f) => ({ ...f, [key]: val }));
    const handleDraftSelect = (key) => (e) => setDraftField(key)(e.target.value);

    const handleApplyFilters = () => {
        setApplied(draft);
        setFiltersOpen(false);
    };

    const handleClearFilters = () => {
        setDraft({ ...INIT_FILTERS });
        setApplied({ ...INIT_FILTERS });
        setQuickPeriod('today');
        setQuickSources([]);
        setQuickSectors([]);
        setQuickRecommendations([]);
        setQuickMaterialityLevel('');
        setQuickRadarKeywords([]);
        setRadarClassTab(0);
        setRadarAllPage(0);
        setRadarAllPageSize(10);
    };

    const handleRefresh = () => setApplied((prev) => ({ ...prev }));

    // ── Navegação para Editorial-Intelligence com filtros ──────────────────────
    const buildEINavState = (overrides = {}) => {
        let dateFrom = applied.dateFrom || '';
        let dateTo   = applied.dateTo   || '';
        if (!dateFrom && !dateTo) {
            const range = computePeriodDateRange(quickPeriod);
            if (range) { dateFrom = range.dateFrom; dateTo = range.dateTo; }
        }
        return {
            publishAtStart:   dateFrom,
            publishAtEnd:     dateTo,
            sources:         'sources'         in overrides ? overrides.sources         : quickSources,
            sectors:         expandSectorSelections('sectors' in overrides ? overrides.sectors : quickSectors, filterOptions.sectors),
            recommendations: 'recommendations' in overrides ? overrides.recommendations : quickRecommendations,
            radarKeywords:   'radarKeywords'   in overrides ? overrides.radarKeywords   : quickRadarKeywords,
            isRadar:         'isRadar'         in overrides ? overrides.isRadar         : (activeTab === 0 ? 'true' : ''),
        };
    };

    const goToEI = (overrides = {}) =>
        navigate('/admin/editorial-intelligence', { state: buildEINavState(overrides) });

    const buildDashboardReturnState = () => ({
        activeTab,
        filtersOpen,
        draft: normalizeFilterState(draft),
        applied: normalizeFilterState(applied),
        quickPeriod: normalizeQuickPeriod(quickPeriod),
        quickSources: [...quickSources],
        quickSectors: [...expandedQuickSectors],
        quickRecommendations: [...quickRecommendations],
        quickMaterialityLevel,
        quickRadarKeywords: [...quickRadarKeywords],
        mobileQuickFiltersOpen,
        granularity: normalizeGranularity(granularity),
        radarClassTab,
        radarAllPage,
        radarAllPageSize,
    });

    const goToEditorialIntelligenceDetail = (editorialIntelligenceId) => {
        if (!editorialIntelligenceId) return;
        navigate(`/admin/editorial-intelligence/${editorialIntelligenceId}`, {
            state: {
                fromDashboard: true,
                dashboardState: buildDashboardReturnState(),
            },
        });
    };

    // ── Dados derivados — Overview ─────────────────────────────────────────────
    const overviewTimeSeries = overview?.time_series ?? [];
    const overviewKpi  = overview?.kpis;
    const overviewDist = overview?.distributions;
    const overviewLastUpdates = overview?.last_updates;

    // ── Dados derivados — Radar ────────────────────────────────────────────────
    const radarKpi  = radar?.kpis;
    const radarDist = radar?.distributions;
    const radarLastUpdates = radar?.last_updates ?? [];

    // Parâmetros de filtro para a aba "Todos" do Radar (sem granularity)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const radarAllParams = useMemo(() => buildQuickParams(), [applied, quickPeriod, quickSources, expandedQuickSectors, quickRecommendations, quickMaterialityLevel, quickRadarKeywords]);

    // Flatten time_series by_radar_keyword para o gráfico Radar
    const { radarFlatTS, radarClassKeys } = useMemo(() => {
        const ts = radar?.time_series ?? [];
        if (!ts.length) return { radarFlatTS: [], radarClassKeys: [] };
        const keysSet = new Set();
        ts.forEach((pt) => (pt.by_radar_keyword || []).forEach((c) => keysSet.add(c.radar_keyword)));
        const keysList = [...keysSet];
        const flatTS = ts.map((pt) => {
            const row = { date: pt.date, total: pt.total, avg_confidence: pt.avg_confidence };
            (pt.by_radar_keyword || []).forEach((c) => { row[`cls_${c.radar_keyword}`] = c.total; });
            return row;
        });
        return { radarFlatTS: flatTS, radarClassKeys: keysList };
    }, [radar]);

    // ── Contadores de filtros ativos ───────────────────────────────────────────
    const advancedFiltersCount = useMemo(() => {
        let c = 0;
        if (applied.dateFrom) c += 1;
        if (applied.dateTo) c += 1;
        if (applied.sentiments?.length) c += 1;
        if (applied.minConf !== '') c += 1;
        if (applied.maxConf !== '') c += 1;
        return c;
    }, [applied]);

    const totalActiveFilters =
        advancedFiltersCount
        + (quickSources.length ? 1 : 0)
        + (expandedQuickSectors.length ? 1 : 0)
        + (quickRecommendations.length ? 1 : 0)
        + (quickMaterialityLevel ? 1 : 0)
        + (quickRadarKeywords.length ? 1 : 0);

    const quickFiltersCount =
        (quickSources.length ? 1 : 0)
        + (expandedQuickSectors.length ? 1 : 0)
        + (quickRecommendations.length ? 1 : 0)
        + (quickMaterialityLevel ? 1 : 0)
        + (quickRadarKeywords.length ? 1 : 0)
        + ((quickPeriod && quickPeriod !== 'today') ? 1 : 0);

    // Loading da aba ativa
    const currentLoading = activeTab === 0 ? radarLoading : overviewLoading;
    const activeDataObj  = activeTab === 0 ? radar : overview;
    const radarDistributionColumns = isUnder768 ? 1 : isUpTo1280 ? 2 : 4;
    const overviewDistributionColumns = isUnder768 ? 1 : isUpTo1280 ? 2 : 3;

    const selectSx = {
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#434851' },
        '& .MuiInputLabel-root.Mui-focused': { color: '#434851' },
    };

    const renderMultiValue = (selected, options) => {
        if (!selected.length) return '';
        if (selected.length === 1) return options?.find((o) => o.value === selected[0])?.text || selected[0];
        return `${selected.length} selecionados`;
    };

    const granularityOptions = [
        { key: 'hour', label: 'Hora' },
        { key: 'day', label: 'Dia' },
        { key: 'week', label: 'Semana' },
        { key: 'month', label: 'Mês' },
    ];

    // ── Render da seleção de granularidade (compartilhado entre abas) ──────────
    const GranularityControl = (
        <ButtonGroup size="small" sx={{ '& .MuiButton-root': { textTransform: 'none', fontSize: '0.76rem', px: 1.5, py: 0.4 } }}>
            {granularityOptions.map(({ key, label }) => {
                const disabledForOneDay  = isOneDayPeriod && key !== 'hour';
                const disabledForMultiDay = !isOneDayPeriod && key === 'hour';
                const isDisabled = disabledForOneDay || disabledForMultiDay;
                const isActive   = activeGranularity === key;
                return (
                    <Button
                        key={key}
                        onClick={() => !isDisabled && setGranularity(key)}
                        variant={isActive ? 'contained' : 'outlined'}
                        disabled={isDisabled}
                        sx={isActive
                            ? { backgroundColor: PRIMARY, color: '#fff', '&:hover': { backgroundColor: PRIMARY_DARK }, borderColor: PRIMARY, boxShadow: 'none' }
                            : { borderColor: '#ddd', color: '#666', '&:hover': { borderColor: PRIMARY, color: PRIMARY, backgroundColor: `${PRIMARY}08` }, '&.Mui-disabled': { borderColor: '#eee', color: '#ccc' } }
                        }
                    >
                        {label}
                    </Button>
                );
            })}
        </ButtonGroup>
    );

    return (
        <ResponsiveAdminPage maxWidth={1600}>
            {/* ── Cabeçalho ─────────────────────────────────────────────────── */}
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1 }}>
                        Dashboard
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                        <Box sx={{
                            width: 8, height: 8, borderRadius: '50%',
                            backgroundColor: PRIMARY,
                            animation: 'livePulse 2s ease-in-out infinite',
                            '@keyframes livePulse': {
                                '0%':   { opacity: 1,    boxShadow: `0 0 0 0 ${PRIMARY}80` },
                                '50%':  { opacity: 0.35, boxShadow: `0 0 0 5px ${PRIMARY}00` },
                                '100%': { opacity: 1,    boxShadow: `0 0 0 0 ${PRIMARY}00` },
                            },
                        }} />
                        <Typography variant="caption" sx={{
                            color: PRIMARY, fontWeight: 700,
                            fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase',
                        }}>
                            Ao Vivo
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: '0.78rem' }}>
                        {activeDataObj
                            ? `Período: ${fmtPeriodDateBR(activeDataObj.period_from)} a ${fmtPeriodDateBR(activeDataObj.period_to)}`
                            : 'Período: —'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {totalActiveFilters > 0 && (
                            <Tooltip title="Limpar todos os filtros">
                                <Button
                                    size="small"
                                    onClick={handleClearFilters}
                                    startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
                                    sx={{
                                        textTransform: 'none', color: '#888', fontSize: '0.78rem',
                                        '&:hover': { backgroundColor: '#f5f5f5', color: '#444' },
                                    }}
                                >
                                    Limpar
                                </Button>
                            </Tooltip>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', ml: 'auto' }}>
                        {isQuickFiltersCompact && (
                            <Badge
                                badgeContent={quickFiltersCount}
                                color="primary"
                                sx={{ '& .MuiBadge-badge': { backgroundColor: PRIMARY, fontSize: '0.62rem', minWidth: 16, height: 16 } }}
                            >
                                <Button
                                    size="small"
                                    startIcon={<FilterListIcon />}
                                    onClick={() => setMobileQuickFiltersOpen((prev) => !prev)}
                                    variant={mobileQuickFiltersOpen ? 'contained' : 'outlined'}
                                    sx={mobileQuickFiltersOpen
                                        ? { backgroundColor: PRIMARY, color: '#fff', '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none' }
                                        : { borderColor: '#ddd', color: '#555', textTransform: 'none', '&:hover': { borderColor: PRIMARY, color: PRIMARY, backgroundColor: `${PRIMARY}08` } }
                                    }
                                >
                                    Filtros rápidos
                                </Button>
                            </Badge>
                        )}
                        <Badge
                            badgeContent={advancedFiltersCount}
                            color="primary"
                            sx={{ '& .MuiBadge-badge': { backgroundColor: PRIMARY, fontSize: '0.62rem', minWidth: 16, height: 16 } }}
                        >
                            <Button
                                size="small"
                                startIcon={<FilterListIcon />}
                                onClick={() => setFiltersOpen((o) => !o)}
                                variant={filtersOpen ? 'contained' : 'outlined'}
                                sx={filtersOpen
                                    ? { backgroundColor: PRIMARY, color: '#fff', '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none' }
                                    : { borderColor: '#ddd', color: '#555', textTransform: 'none', '&:hover': { borderColor: PRIMARY, color: PRIMARY, backgroundColor: `${PRIMARY}08` } }
                                }
                            >
                                Filtros avançados
                            </Button>
                        </Badge>
                        <Tooltip title="Atualizar dados">
                            <IconButton
                                onClick={handleRefresh}
                                size="small"
                                disabled={currentLoading}
                                sx={{
                                    border: '1px solid #ddd', borderRadius: '6px',
                                    '&:hover': { borderColor: PRIMARY, color: PRIMARY, backgroundColor: `${PRIMARY}08` },
                                }}
                            >
                                <RefreshIcon fontSize="small" sx={{
                                    animation: currentLoading ? 'spin 1s linear infinite' : 'none',
                                    '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                                }} />
                            </IconButton>
                        </Tooltip>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ── Abas ──────────────────────────────────────────────────────── */}
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                    mb: 2.5,
                    '& .MuiTabs-indicator': { backgroundColor: PRIMARY, height: 2 },
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.88rem', color: '#777' },
                    '& .Mui-selected': { color: `${PRIMARY} !important` },
                }}
            >
                <Tab label="Radar" />
                <Tab label="Overview" />
            </Tabs>

            {/* ── Painel de Filtros Avançados (colapsável, compartilhado) ───── */}
            <Collapse in={filtersOpen}>
                <Paper sx={{
                    p: 2.5, mb: 2.5, borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
                }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333', mb: 1.8, fontSize: '0.85rem' }}>
                        Filtros Avançados
                    </Typography>
                    <Grid container spacing={1.5} alignItems="flex-end">
                        <Grid item xs={6} sm={4} md={2}>
                            <TextField label="De" type="date" size="small" fullWidth value={draft.dateFrom}
                                onChange={(e) => setDraftField('dateFrom')(e.target.value)}
                                InputLabelProps={{ shrink: true }} sx={selectSx} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <TextField label="Até" type="date" size="small" fullWidth value={draft.dateTo}
                                onChange={(e) => setDraftField('dateTo')(e.target.value)}
                                InputLabelProps={{ shrink: true }} sx={selectSx} />
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <FormControl size="small" fullWidth sx={selectSx}>
                                <InputLabel>Sentimento</InputLabel>
                                <Select multiple value={draft.sentiments} onChange={handleDraftSelect('sentiments')}
                                    input={<OutlinedInput label="Sentimento" />}
                                    renderValue={(s) => renderMultiValue(s, filterOptions.document_sentiments)}>
                                    {filterOptions.document_sentiments?.map((o) => (
                                        <MenuItem key={o.value} value={o.value}>
                                            <Checkbox checked={draft.sentiments.includes(o.value)} size="small" />
                                            <ListItemText primary={o.text} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <TextField label="Conf. mínima" type="number" size="small" fullWidth
                                inputProps={{ min: 0, max: 1, step: 0.05 }}
                                value={draft.minConf}
                                onChange={(e) => setDraftField('minConf')(e.target.value)}
                                placeholder="0.0 – 1.0" sx={selectSx} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <TextField label="Conf. máxima" type="number" size="small" fullWidth
                                inputProps={{ min: 0, max: 1, step: 0.05 }}
                                value={draft.maxConf}
                                onChange={(e) => setDraftField('maxConf')(e.target.value)}
                                placeholder="0.0 – 1.0" sx={selectSx} />
                        </Grid>
                        <Grid item xs={12}>
                            <Divider sx={{ mb: 1.5 }} />
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button size="small" onClick={() => setDraft(INIT_FILTERS)} sx={{ color: '#888', textTransform: 'none' }}>
                                    Resetar
                                </Button>
                                <Button size="small" variant="contained" onClick={handleApplyFilters}
                                    sx={{ backgroundColor: PRIMARY, '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none' }}>
                                    Aplicar
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Collapse>

            {/* ── Filtros Rápidos (compartilhados entre abas) ────────────────── */}
            <Collapse in={!isQuickFiltersCompact || mobileQuickFiltersOpen}>
                <Paper sx={{ p: 2, mb: 2.5, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', border: '1px solid #f0f0f0' }}>
                {/* Linha 1: Período · Fonte · Materialidade */}
                <Grid container spacing={2} sx={{ mb: 1.5 }}>
                    <Grid item xs={12} sm={4}>
                        <ChipRow label="Período">
                            {PERIOD_OPTIONS.map(({ key, label: lbl }) => {
                                const disabled = Boolean(applied.dateFrom || applied.dateTo);
                                const selected = !disabled && quickPeriod === key;
                                return (
                                    <Box key={key} component="span" onClick={() => !disabled && setQuickPeriod(key)} sx={miniChipSx(selected, disabled)}>
                                        {lbl}
                                    </Box>
                                );
                            })}
                        </ChipRow>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <ChipRow label="Fonte">
                            <Box component="span" onClick={() => setQuickSources([])} sx={miniChipSx(!quickSources.length)}>Todas</Box>
                            {filterOptions.sources.map((o, idx) => {
                                const v = o.value ?? o;
                                const txt = o.text ?? o;
                                const sel = quickSources.includes(v);
                                return (
                                    <Box key={idx} component="span" sx={miniChipSx(sel)}
                                        onClick={() => setQuickSources(sel ? quickSources.filter((x) => x !== v) : [...quickSources, v])}>
                                        {txt}
                                    </Box>
                                );
                            })}
                        </ChipRow>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <ChipRow label="Materialidade">
                            <Box
                                component="span"
                                onClick={() => setQuickMaterialityLevel(quickMaterialityLevel === 'alta' ? '' : 'alta')}
                                sx={miniChipSx(quickMaterialityLevel === 'alta')}
                            >
                                Alta
                            </Box>
                        </ChipRow>
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 1.5 }} />

                {/* Linha 2: Radar */}
                <Box sx={{ mb: 1.5 }}>
                    <ChipRow label="Radar">
                        <Box component="span" onClick={() => setQuickRadarKeywords([])} sx={miniChipSx(!quickRadarKeywords.length)}>Todos</Box>
                        {filterOptions.radar_keywords.map((o, idx) => {
                            const v = o.value ?? o;
                            const txt = o.text ?? o;
                            const sel = quickRadarKeywords.includes(v);
                            return (
                                <Box key={idx} component="span" sx={miniChipSx(sel)}
                                    onClick={() => setQuickRadarKeywords(sel ? quickRadarKeywords.filter((x) => x !== v) : [...quickRadarKeywords, v])}>
                                    {txt}
                                </Box>
                            );
                        })}
                    </ChipRow>
                </Box>

                <Divider sx={{ mb: 1.5 }} />

                {/* Linha 3: Recomendação */}
                <Box sx={{ mb: 1.5 }}>
                    <ChipRow label="Recomendação">
                        <Box component="span" onClick={() => setQuickRecommendations([])} sx={miniChipSx(!quickRecommendations.length)}>Todas</Box>
                        {filterOptions.publication_recommendation_primary_areas.map((o, idx) => {
                            const v = o.value ?? o;
                            const txt = o.text ?? o;
                            const sel = quickRecommendations.includes(v);
                            return (
                                <Box key={idx} component="span" sx={miniChipSx(sel)}
                                    onClick={() => setQuickRecommendations(sel ? quickRecommendations.filter((x) => x !== v) : [...quickRecommendations, v])}>
                                    {txt}
                                </Box>
                            );
                        })}
                    </ChipRow>
                </Box>

                <Divider sx={{ mb: 1.5 }} />

                {/* Linha 4: Setor */}
                <ChipRow label="Setor">
                    <Box component="span" onClick={() => setQuickSectors([])} sx={miniChipSx(!expandedQuickSectors.length)}>Todos</Box>
                    {sectorFilterGroups.map((group) => {
                        const sel = isSectorGroupSelected(quickSectors, group);
                        return (
                            <Box key={group.key} component="span" sx={miniChipSx(sel)}
                                onClick={() => setQuickSectors((selected) => toggleSectorGroupSelection(selected, group))}>
                                {group.label}
                            </Box>
                        );
                    })}
                </ChipRow>
                </Paper>
            </Collapse>

            {/* ════════════════════════════════════════════════════════════════
                ABA: RADAR
            ════════════════════════════════════════════════════════════════ */}
            {activeTab === 0 && (
                <>
                    {radarLoading && !radar ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                            <CircularProgress sx={{ color: PRIMARY }} />
                        </Box>
                    ) : (
                        <>
                            {/* ── KPIs ──────────────────────────────────────────────── */}
                            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                                <Grid item xs={12} sm={4}>
                                    <KpiCard
                                        title="Pautas Monitoradas"
                                        value={radarKpi?.total_articles?.toLocaleString('pt-BR') ?? '—'}
                                        helper={radarKpi?.total_articles != null ? `de um total de ${radarKpi.total_articles.toLocaleString('pt-BR')}` : 'total no período'}
                                        change={radarKpi?.total_articles_change_percent}
                                        icon={<ArticleIcon />}
                                        accentColor={PRIMARY}
                                        onClick={() => goToEI()}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <KpiCard
                                        title="Confiança Média"
                                        value={fmtConf(radarKpi?.avg_confidence)}
                                        helper="modelo de classificação"
                                        change={radarKpi?.avg_confidence_change_percent}
                                        icon={<SpeedIcon />}
                                        accentColor="#10b981"
                                        onClick={() => goToEI()}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <KpiCard
                                        title="Fontes Ativas"
                                        value={radarKpi?.active_sources?.toLocaleString('pt-BR') ?? '—'}
                                        helper="fontes com publicações"
                                        icon={<HubIcon />}
                                        accentColor="#1a56db"
                                        onClick={() => goToEI()}
                                    />
                                </Grid>
                            </Grid>

                            {/* ── Série Temporal Radar (12 colunas, linhas por classificação) */}
                            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                                <Grid item xs={12}>
                                    <Paper sx={{
                                        p: 2.5, borderRadius: '12px', height: '100%',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
                                    }}>
                                        <SectionHead
                                            title="Série Temporal"
                                            subtitle="Evolução de artigos por classificação no período"
                                            action={GranularityControl}
                                        />
                                        {radarLoading ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                                                <CircularProgress size={28} sx={{ color: PRIMARY }} />
                                            </Box>
                                        ) : !radarFlatTS.length ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220, color: '#ccc', fontSize: '0.85rem' }}>
                                                Sem dados para o período selecionado
                                            </Box>
                                        ) : (
                                            <ResponsiveContainer width="100%" height={300}>
                                                <ComposedChart data={radarFlatTS} margin={{ top: 10, right: 60, bottom: 5, left: 0 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f4" />
                                                    <XAxis
                                                        dataKey="date"
                                                        tick={{ fontSize: 11, fill: '#aaa' }}
                                                        tickLine={false}
                                                        axisLine={false}
                                                        interval="preserveStartEnd"
                                                        tickFormatter={(v) => fmtTimeTick(v, activeGranularity)}
                                                    />
                                                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#aaa' }} tickLine={false} axisLine={false} />
                                                    <YAxis
                                                        yAxisId="right"
                                                        orientation="right"
                                                        domain={[0, 1]}
                                                        tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                                                        tick={{ fontSize: 11, fill: '#10b981' }}
                                                        tickLine={false}
                                                        axisLine={false}
                                                    />
                                                    <RTooltip content={<ChartTooltip labelFmt={(v) => fmtTimeTick(v, activeGranularity)} />} />
                                                    <Legend wrapperStyle={{ fontSize: '0.78rem', paddingTop: 10 }} iconType="circle" />

                                                    {/* Linha total */}
                                                    <Line
                                                        yAxisId="left"
                                                        type="monotone"
                                                        dataKey="total"
                                                        name="Total"
                                                        stroke={PRIMARY}
                                                        strokeWidth={2.5}
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                    {/* Confiança média (eixo direito) */}
                                                    <Line
                                                        yAxisId="right"
                                                        type="monotone"
                                                        dataKey="avg_confidence"
                                                        name="Confiança Média"
                                                        stroke="#10b981"
                                                        strokeWidth={1.8}
                                                        strokeDasharray="4 3"
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                    {/* Linhas por classificação */}
                                                    {radarClassKeys.map((cls, i) => (
                                                        <Line
                                                            key={cls}
                                                            yAxisId="left"
                                                            type="monotone"
                                                            dataKey={`cls_${cls}`}
                                                            name={cls}
                                                            stroke={CLASS_COLORS[i % CLASS_COLORS.length]}
                                                            strokeWidth={1.5}
                                                            dot={false}
                                                            connectNulls
                                                        />
                                                    ))}
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* ── Distribuições Radar ─────────────────────────────── */}
                            {!radarLoading && (
                                <Box
                                    sx={{
                                        mb: 2.5,
                                        display: 'grid',
                                        gap: 2,
                                        gridTemplateColumns: `repeat(${radarDistributionColumns}, minmax(0, 1fr))`,
                                    }}
                                >
                                    <Box>
                                        <DistSourceBar
                                            data={radarDist?.by_source}
                                            onItemClick={(label) => {
                                                const opt = filterOptions.sources.find((o) => (o.text ?? o) === label);
                                                const value = opt ? (opt.value ?? opt) : label;
                                                goToEI({ sources: [value], sectors: [], recommendations: [] });
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <DistHorizBar
                                            title="Setor"
                                            data={radarDist?.by_sector}
                                            color="#06b6d4"
                                            yAxisWidth={110}
                                            maxLabelChars={16}
                                            onItemClick={(sec) => goToEI({ sources: [], sectors: [sec], recommendations: [] })}
                                        />
                                    </Box>
                                    <Box>
                                        <DistHorizBar
                                            title="Recomendação"
                                            data={radarDist?.by_publication_recommendation}
                                            color="#8b5cf6"
                                            yAxisWidth={110}
                                            maxLabelChars={16}
                                            onItemClick={(rec) => goToEI({ sources: [], sectors: [], recommendations: [rec] })}
                                        />
                                    </Box>
                                    <Box>
                                        <DistBubble
                                            title="Radar"
                                            data={radarDist?.by_radar_keyword}
                                            onItemClick={(label) => {
                                                const opt = filterOptions.radar_keywords.find((o) => (o.text ?? o) === label);
                                                const value = opt ? (opt.value ?? opt) : label;
                                                goToEI({ sources: [], sectors: [], recommendations: [], radarKeywords: [value] });
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}

                            {/* ── Últimas Atualizações Radar ──────────────────────── */}
                            {!radarLoading && (
                                <Box ref={lastUpdatesSectionRef}>
                                    <RadarLastUpdates
                                        groups={radarLastUpdates}
                                        filterParams={radarAllParams}
                                        hasHydratedData={Boolean(radar)}
                                        classTab={radarClassTab}
                                        onClassTabChange={setRadarClassTab}
                                        radarAllPage={radarAllPage}
                                        radarAllPageSize={radarAllPageSize}
                                        onRadarAllPageChange={setRadarAllPage}
                                        onRadarAllPageSizeChange={setRadarAllPageSize}
                                        onOpenItem={goToEditorialIntelligenceDetail}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}

            {/* ════════════════════════════════════════════════════════════════
                ABA: OVERVIEW
            ════════════════════════════════════════════════════════════════ */}
            {activeTab === 1 && (
                <>
                    {overviewLoading && !overview ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                            <CircularProgress sx={{ color: PRIMARY }} />
                        </Box>
                    ) : (
                        <>
                            {/* ── KPIs ──────────────────────────────────────────────── */}
                            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                                <Grid item xs={12} sm={4}>
                                    <KpiCard
                                        title="Pautas Monitoradas"
                                        value={overviewKpi?.total_articles?.toLocaleString('pt-BR') ?? '—'}
                                        helper="total no período"
                                        change={overviewKpi?.total_articles_change_percent}
                                        icon={<ArticleIcon />}
                                        accentColor={PRIMARY}
                                        onClick={() => goToEI()}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <KpiCard
                                        title="Confiança Média"
                                        value={fmtConf(overviewKpi?.avg_confidence)}
                                        helper="modelo de classificação"
                                        change={overviewKpi?.avg_confidence_change_percent}
                                        icon={<SpeedIcon />}
                                        accentColor="#10b981"
                                        onClick={() => goToEI()}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <KpiCard
                                        title="Fontes Ativas"
                                        value={overviewKpi?.active_sources?.toLocaleString('pt-BR') ?? '—'}
                                        helper="fontes com publicações"
                                        icon={<HubIcon />}
                                        accentColor="#1a56db"
                                        onClick={() => goToEI()}
                                    />
                                </Grid>
                            </Grid>

                            {/* ── Série Temporal (esq) + Por Fonte (dir) ────────────── */}
                            <Grid container spacing={2} sx={{ mb: 2.5 }} alignItems="stretch">
                                <Grid item xs={12} md={8}>
                                    <Paper sx={{
                                        p: 2.5, borderRadius: '12px', height: '100%',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
                                    }}>
                                        <SectionHead
                                            title="Série Temporal"
                                            subtitle="Evolução de artigos e confiança no período"
                                            action={GranularityControl}
                                        />
                                        {overviewLoading ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                                                <CircularProgress size={28} sx={{ color: PRIMARY }} />
                                            </Box>
                                        ) : !overviewTimeSeries.length ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220, color: '#ccc', fontSize: '0.85rem' }}>
                                                Sem dados para o período selecionado
                                            </Box>
                                        ) : (
                                            <ResponsiveContainer width="100%" height={260}>
                                                <ComposedChart data={overviewTimeSeries} margin={{ top: 10, right: 60, bottom: 5, left: 0 }}>
                                                    <defs>
                                                        <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor={PRIMARY} stopOpacity={0.35} />
                                                            <stop offset="95%" stopColor={PRIMARY} stopOpacity={0.02} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f4" />
                                                    <XAxis
                                                        dataKey="date"
                                                        tick={{ fontSize: 11, fill: '#aaa' }}
                                                        tickLine={false}
                                                        axisLine={false}
                                                        interval="preserveStartEnd"
                                                        tickFormatter={(v) => fmtTimeTick(v, activeGranularity)}
                                                    />
                                                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#aaa' }} tickLine={false} axisLine={false} />
                                                    <YAxis
                                                        yAxisId="right"
                                                        orientation="right"
                                                        domain={[0, 1]}
                                                        tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                                                        tick={{ fontSize: 11, fill: '#10b981' }}
                                                        tickLine={false}
                                                        axisLine={false}
                                                    />
                                                    <RTooltip content={<ChartTooltip labelFmt={(v) => fmtTimeTick(v, activeGranularity)} />} />
                                                    <Legend wrapperStyle={{ fontSize: '0.78rem', paddingTop: 10 }} iconType="circle" />
                                                    <Area yAxisId="left" type="monotone" dataKey="total" name="Total de Artigos" fill="url(#totalGrad)" stroke={PRIMARY} strokeWidth={2.2} dot={false} />
                                                    <Line
                                                        yAxisId="right"
                                                        type="monotone"
                                                        dataKey="avg_confidence"
                                                        name="Confiança Média"
                                                        stroke="#10b981"
                                                        strokeWidth={2}
                                                        strokeDasharray="4 3"
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        )}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    {overviewLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 6 }}>
                                            <CircularProgress size={24} sx={{ color: PRIMARY }} />
                                        </Box>
                                    ) : (
                                        <DistSourceBar
                                            data={overviewDist?.by_source}
                                            onItemClick={(label) => {
                                                const opt = filterOptions.sources.find((o) => (o.text ?? o) === label);
                                                const value = opt ? (opt.value ?? opt) : label;
                                                goToEI({ sources: [value], sectors: [], recommendations: [] });
                                            }}
                                        />
                                    )}
                                </Grid>
                            </Grid>

                            {/* ── Distribuições: Setor · Recomendação · Bolhas ─────── */}
                            {!overviewLoading && (
                                <Box
                                    sx={{
                                        mb: 2.5,
                                        display: 'grid',
                                        gap: 2,
                                        gridTemplateColumns: `repeat(${overviewDistributionColumns}, minmax(0, 1fr))`,
                                    }}
                                >
                                    <Box>
                                        <DistHorizBar
                                            title="Setor"
                                            data={overviewDist?.by_sector}
                                            color="#06b6d4"
                                            yAxisWidth={130}
                                            maxLabelChars={18}
                                            onItemClick={(sec) => goToEI({ sources: [], sectors: [sec], recommendations: [] })}
                                        />
                                    </Box>
                                    <Box>
                                        <DistHorizBar
                                            title="Recomendação"
                                            data={overviewDist?.by_publication_recommendation}
                                            color="#8b5cf6"
                                            yAxisWidth={130}
                                            maxLabelChars={18}
                                            onItemClick={(rec) => goToEI({ sources: [], sectors: [], recommendations: [rec] })}
                                        />
                                    </Box>
                                    <Box>
                                        <DistBubble
                                            title="Recomendação — Bolhas"
                                            data={overviewDist?.by_publication_recommendation}
                                            onItemClick={(rec) => goToEI({ sources: [], sectors: [], recommendations: [rec] })}
                                        />
                                    </Box>
                                </Box>
                            )}

                            {/* ── Últimas Atualizações ──────────────────────────────── */}
                            {!overviewLoading && (
                                <Box ref={lastUpdatesSectionRef}>
                                    <LatestUpdatesTable
                                        items={overviewLastUpdates}
                                        onOpenItem={goToEditorialIntelligenceDetail}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}
        </ResponsiveAdminPage>
    );
}
